import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { fetchUsers } from '@/features/users/api'
import LoadingState from '@/components/common/LoadingState'
import EmptyState from '@/components/common/EmptyState'
import ErrorState from '@/components/common/ErrorState'

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  staff: 'Staff',
  client: 'Client',
  other: 'Other',
}

/** Ports includes/users.html — the user directory, grouped by role in the
 * legacy template via three separate Jinja loops; here it's one list with a
 * role filter, same underlying grouping. */
export default function UsersPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: fetchUsers,
  })
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [search, setSearch] = useState('')

  const filtered = (data ?? []).filter((u) => {
    if (roleFilter !== 'all' && u.role_type !== roleFilter) return false
    if (!search) return true
    const q = search.toLowerCase()
    return (
      u.email.toLowerCase().includes(q) ||
      (u.firstname ?? '').toLowerCase().includes(q) ||
      (u.lastname ?? '').toLowerCase().includes(q)
    )
  })

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink">Users</h1>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email…"
          className="h-11 w-full max-w-sm rounded-full border border-transparent bg-shell px-[18px] text-sm text-text-primary focus:border-brand focus:outline-none"
        />
        {['all', 'admin', 'staff', 'client', 'other'].map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRoleFilter(r)}
            className={`h-9 rounded-full px-4 text-sm font-semibold transition-colors ${
              roleFilter === r ? 'bg-brand text-white' : 'border border-nav-line text-text-primary hover:bg-nav-hover'
            }`}
          >
            {r === 'all' ? 'All' : ROLE_LABEL[r]}
          </button>
        ))}
      </div>

      {isLoading && <LoadingState label="Loading users…" />}
      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load users.'}
          onRetry={() => refetch()}
        />
      )}
      {data && filtered.length === 0 && <EmptyState message="No users match." />}

      {filtered.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-nav-line bg-card">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="border-b border-nav-line text-left text-xs text-nav-muted">
                <th className="px-4 py-3.5 font-semibold">Name</th>
                <th className="px-4 py-3.5 font-semibold">Email</th>
                <th className="px-4 py-3.5 font-semibold">Company</th>
                <th className="px-4 py-3.5 font-semibold">Role</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id} className="border-b border-nav-line last:border-0 hover:bg-nav-hover">
                  <td className="px-4 py-3.5">
                    <Link to={`/users/${u.user_id}`} className="text-accent hover:underline">
                      {u.firstname} {u.lastname}
                    </Link>
                  </td>
                  <td className="px-4 py-3.5 text-ink">{u.email}</td>
                  <td className="px-4 py-3.5 text-ink">{u.company_name ?? '—'}</td>
                  <td className="px-4 py-3.5 text-ink">{ROLE_LABEL[u.role_type]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
